import { css } from '@emotion/react';
import { without } from 'lodash';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Takeover from '../../Takeover';
import { colors, spacing, fontStyles } from '../../../styles';
import Button from '../../Button';
import { TimeRange } from '../../../types';
import Select from '../../Select';
import TimeRangeRadios from '../../TimeRangeRadios';
import { ColumnSelection, ColumnSelectionType } from '../columns';
import BottomLineExplanationTakeover from '../../BottomLineExplanationTakeover';
import leftLabel from './left.svg';
import middleLabel from './middle.svg';
import rightLabel from './right.svg';

interface Props {
  isShown: boolean;
  columnSelections: ColumnSelection[];
  onClose: () => void;
  onChange: (index: number, value: ColumnSelection) => void;
  hideMiddleColumn?: boolean;
  isPlaidPortfolio?: boolean;
}

const columnIdToTranslationKey: Partial<Record<ColumnSelectionType, string>> = {
  [ColumnSelectionType.None]: 'portfolios.columns.none',
  [ColumnSelectionType.Name]: 'portfolios.columns.name',
  [ColumnSelectionType.Symbol]: 'portfolios.columns.symbol',
  [ColumnSelectionType.ChangePercent]: 'portfolios.columns.changePercent',
  [ColumnSelectionType.BottomLine]: 'portfolios.columns.bottomLine',
  [ColumnSelectionType.LatestPrice]: 'portfolios.columns.latestPrice',
  [ColumnSelectionType.RevenueGrowth1y]: 'portfolios.columns.revenueGrowth1y',
  [ColumnSelectionType.PercentMarketCapTraded1d]: 'portfolios.columns.tradedPercent1d',
  [ColumnSelectionType.PriceToEarnings]: 'portfolios.columns.priceToEarnings',
  [ColumnSelectionType.PriceToSales]: 'portfolios.columns.priceToSales',
  [ColumnSelectionType.PriceToSalesGrowth]: 'portfolios.columns.psGrowth',
  [ColumnSelectionType.Week52Range]: 'portfolios.columns.week52HighLow',
  [ColumnSelectionType.MarketCap]: 'portfolios.columns.marketCap',
  [ColumnSelectionType.CashflowYield]: 'portfolios.columns.cashflowYield',
  [ColumnSelectionType.CapexToRevenue]: 'portfolios.columns.capexToRevenue',
  [ColumnSelectionType.EvToGrossProfit]: 'portfolios.columns.evToGrossProfit',
  [ColumnSelectionType.InsidersPercentTraded3m]: 'portfolios.columns.insiderTrades3m',
  // Plaid-specific column options
  [ColumnSelectionType.CurrentValue]: 'portfolios.columns.currentValue',
  [ColumnSelectionType.CostBasis]: 'portfolios.columns.costBasis',
  [ColumnSelectionType.ReturnAmount]: 'portfolios.columns.returnAmount',
  [ColumnSelectionType.ReturnPercent]: 'portfolios.columns.returnPercent',
};

// Plaid-specific column types that should only show for Plaid portfolios
const plaidOnlyColumns = [
  ColumnSelectionType.CurrentValue,
  ColumnSelectionType.CostBasis,
  ColumnSelectionType.ReturnAmount,
  ColumnSelectionType.ReturnPercent,
];

const Field: FC<Omit<Props, 'isShown' | 'onClose'> & { index: number }> = ({
  index,
  columnSelections,
  onChange,
  isPlaidPortfolio = false,
}) => {
  const { t } = useTranslation();
  const [isBottomLineExplanationShown, setIsBottomLineExplanationShown] = useState(false);
  const label = [
    { src: leftLabel, alt: t('portfolios.columns.leftLabel') },
    { src: middleLabel, alt: t('portfolios.columns.middleLabel') },
    { src: rightLabel, alt: t('portfolios.columns.rightLabel') },
  ];

  // Build options list based on column index and portfolio type
  const getOptions = () => {
    if (index === 0) {
      return [ColumnSelectionType.Name, ColumnSelectionType.Symbol];
    }

    // Get all column types except Name and Symbol
    let allOptions = without(
      Object.values(ColumnSelectionType),
      ColumnSelectionType.None,
      ColumnSelectionType.Name,
      ColumnSelectionType.Symbol,
    );

    // Filter out Plaid-only columns for non-Plaid portfolios
    if (!isPlaidPortfolio) {
      allOptions = without(allOptions, ...plaidOnlyColumns);
    }

    return [ColumnSelectionType.None, ...allOptions];
  };

  const options = getOptions();

  return (
    <div
      css={css`
        display: flex;
        margin-bottom: ${spacing.deka};
      `}
      role="group"
      aria-labelledby={`columnActionSheetLabel-${index}`}
    >
      <BottomLineExplanationTakeover
        isShown={isBottomLineExplanationShown}
        onClose={() => setIsBottomLineExplanationShown(false)}
      />
      <span
        css={css`
          padding-top: 6px;
        `}
      >
        <img id={`columnActionSheetLabel-${index}`} {...label[index]} />
      </span>
      <div
        css={css`
          flex: 1;
          margin-left: ${spacing.deci};
        `}
      >
        <Select
          css={css`
            margin-bottom: ${spacing.deci};
          `}
          value={columnSelections[index].type}
          onChange={(event) => {
            const is1WeekAlreadySelected = columnSelections.some(
              (selection) =>
                selection.timeRange === TimeRange.weeks1 && selection.type === ColumnSelectionType.ChangePercent,
            );
            const timeRange =
              event.target.value !== ColumnSelectionType.ChangePercent
                ? undefined
                : is1WeekAlreadySelected
                  ? TimeRange.months1
                  : TimeRange.weeks1;
            onChange(index, { type: event.target.value as ColumnSelectionType, timeRange });
          }}
        >
          {options.map((option) => (
            <option key={option} value={option}>
              {t(columnIdToTranslationKey[option] || '')}
            </option>
          ))}
        </Select>
        {columnSelections[index].type === ColumnSelectionType.BottomLine && (
          <Button
            onClick={() => setIsBottomLineExplanationShown(true)}
            css={css`
              ${fontStyles.flea}
            `}
          >
            {t('portfolios.columns.whatsBottomLine')}
          </Button>
        )}
        {columnSelections[index].type === ColumnSelectionType.ChangePercent && (
          <TimeRangeRadios
            timeRanges={Object.values(TimeRange)}
            selectedTimeRange={columnSelections[index].timeRange}
            onRadioChange={(timeRange) => {
              onChange(index, { type: columnSelections[index].type, timeRange });
            }}
          />
        )}
      </div>
    </div>
  );
};

const ColumnTakeover: FC<Props> = ({
  isShown,
  columnSelections,
  onClose,
  onChange,
  hideMiddleColumn = false,
  isPlaidPortfolio = false,
}) => {
  const { t } = useTranslation();

  if (columnSelections?.length === 0) return <div />;

  return (
    <Takeover title={t('portfolios.columns.title')} onClose={onClose} isShown={isShown} name="ChangeColumns">
      <form
        css={css`
          color: ${colors.liquorice};
        `}
        onSubmit={(event) => {
          event.preventDefault();
          onClose();
        }}
      >
        <Field index={0} columnSelections={columnSelections} onChange={onChange} isPlaidPortfolio={isPlaidPortfolio} />
        {!hideMiddleColumn && (
          <Field
            index={1}
            columnSelections={columnSelections}
            onChange={onChange}
            isPlaidPortfolio={isPlaidPortfolio}
          />
        )}
        <Field index={2} columnSelections={columnSelections} onChange={onChange} isPlaidPortfolio={isPlaidPortfolio} />

        <Button
          variant="secondary"
          block
          type="submit"
          css={css`
            margin-top: ${spacing.kilo};
          `}
        >
          {t('portfolios.columns.done')}
        </Button>
      </form>
    </Takeover>
  );
};

export default ColumnTakeover;
